import { TFunction } from 'i18next';
import { AuditOutcome } from 'models/internalAudit';

// Mirrors InternalAuditControlAuditItemStatus (API).
export enum AiAuditItemStatus {
  NotRun = 0,
  Waiting = 1,
  Running = 2,
  Ready = 3,
  Failed = 4,
  Skipped = 5,
}

// Mirrors ProcessRunStatus (API).
export enum AiAuditRunStatus {
  NotStarted = 0,
  InProgress = 1,
  Completed = 2,
  Cancelled = 3,
  Failed = 4,
}

// Mirrors ControlAuditReportStatus (API).
export enum AiControlAuditReportStatus {
  Failed = 0,
  Ready = 1,
  PartiallyReady = 2,
  NotReady = 3,
  NotApplicable = 4,
}

// The AI's verdict proposed as an audit outcome. Deliberately NEVER NonConformityMajor: that
// judgement stays with the human auditor. Failed/NotApplicable propose nothing.
export const mapAiReportStatusToOutcome = (status: AiControlAuditReportStatus | undefined): AuditOutcome | undefined => {
  switch (status) {
    case AiControlAuditReportStatus.Ready:
      return AuditOutcome.Conform;
    case AiControlAuditReportStatus.PartiallyReady:
      return AuditOutcome.Observation;
    case AiControlAuditReportStatus.NotReady:
      return AuditOutcome.NonConformityMinor;
    default:
      return undefined;
  }
};

// Literal keys on purpose: the unused-translations test scans for them.
export const getAiAuditItemStatusText = (status: AiAuditItemStatus, t: TFunction<string[]>): string => {
  switch (status) {
    case AiAuditItemStatus.Waiting:
      return t('audit:Execute.AiAudit.Status.Waiting');
    case AiAuditItemStatus.Running:
      return t('audit:Execute.AiAudit.Status.Running');
    case AiAuditItemStatus.Ready:
      return t('audit:Execute.AiAudit.Status.Ready');
    case AiAuditItemStatus.Failed:
      return t('audit:Execute.AiAudit.Status.Failed');
    case AiAuditItemStatus.Skipped:
      return t('audit:Execute.AiAudit.Status.Skipped');
    default:
      return t('audit:Execute.AiAudit.Status.NotRun');
  }
};

// Literal key on purpose: the unused-translations test scans for it.
export const getAiAuditNotAssessedText = (t: TFunction<string[]>): string =>
  t('audit:Execute.AiAudit.Verdict.NotAssessed');

export const getAiAuditVerdictText = (
  status: AiControlAuditReportStatus | undefined,
  t: TFunction<string[]>,
): string => {
  switch (status) {
    case AiControlAuditReportStatus.Ready:
      return t('audit:Execute.AiAudit.Verdict.Ready');
    case AiControlAuditReportStatus.PartiallyReady:
      return t('audit:Execute.AiAudit.Verdict.PartiallyReady');
    case AiControlAuditReportStatus.NotReady:
      return t('audit:Execute.AiAudit.Verdict.NotReady');
    case AiControlAuditReportStatus.NotApplicable:
      return t('audit:Execute.AiAudit.Verdict.NotApplicable');
    case AiControlAuditReportStatus.Failed:
      return t('audit:Execute.AiAudit.Verdict.Failed');
    default:
      return '';
  }
};

export class InternalAuditAiAuditItem {
  entityType: number;

  entityId: number;

  status: AiAuditItemStatus;

  documentationStatus: AiControlAuditReportStatus | undefined;

  evidenceStatus: AiControlAuditReportStatus | undefined;

  // False when the report was a guard MESSAGE report (a reason, not an assessment). Undefined for
  // results stored before this field existed: treated as assessed, the old behavior.
  documentationAssessed: boolean | undefined;

  evidenceAssessed: boolean | undefined;

  // Short per-mode summary/reason line, for the grid tooltip.
  documentationSummary: string;

  evidenceSummary: string;

  generatedOn: Date | undefined;

  error: string;

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.status = AiAuditItemStatus.NotRun;
    this.documentationStatus = undefined;
    this.evidenceStatus = undefined;
    this.documentationAssessed = undefined;
    this.evidenceAssessed = undefined;
    this.documentationSummary = '';
    this.evidenceSummary = '';
    this.generatedOn = undefined;
    this.error = '';
  }

  // Whether the AI actually evaluated anything: a run whose modes all came back as guard message
  // reports ("no monitoring tasks", "not monitored") assessed nothing and must not read as a verdict.
  hasAssessment(): boolean {
    return (
      (this.documentationStatus !== undefined && this.documentationAssessed !== false) ||
      (this.evidenceStatus !== undefined && this.evidenceAssessed !== false)
    );
  }

  // The worst verdict over the ASSESSED modes only; undefined when nothing was assessed.
  getWorstAssessedStatus(): AiControlAuditReportStatus | undefined {
    const item = new InternalAuditAiAuditItem();
    item.documentationStatus = this.documentationAssessed !== false ? this.documentationStatus : undefined;
    item.evidenceStatus = this.evidenceAssessed !== false ? this.evidenceStatus : undefined;

    return item.getWorstStatus();
  }

  // The per-mode summaries as one tooltip text; empty when the run carried none.
  getSummaryText(): string {
    return [this.documentationSummary, this.evidenceSummary].filter((summary) => summary).join('\n\n');
  }

  // The worst of the two mode verdicts drives the grid color: an item is only as good as its
  // weakest audit. Only the three real verdicts take part, like the server's FrameworkAuditRollup:
  // Failed and NotApplicable are not assessments, so they never become "the worst verdict" and
  // never propose an outcome for take-over.
  getWorstStatus(): AiControlAuditReportStatus | undefined {
    const statuses = [this.documentationStatus, this.evidenceStatus].filter(
      (status) =>
        status === AiControlAuditReportStatus.Ready ||
        status === AiControlAuditReportStatus.PartiallyReady ||
        status === AiControlAuditReportStatus.NotReady,
    ) as AiControlAuditReportStatus[];
    if (statuses.length === 0) return undefined;

    const rank = (status: AiControlAuditReportStatus): number => {
      switch (status) {
        case AiControlAuditReportStatus.NotReady:
          return 3;
        case AiControlAuditReportStatus.PartiallyReady:
          return 2;
        case AiControlAuditReportStatus.Ready:
          return 1;
        default:
          return 0;
      }
    };

    return statuses.reduce((worst, status) => (rank(status) > rank(worst) ? status : worst));
  }
}

export class InternalAuditAiAuditStatus {
  internalAuditId: number;

  runStatus: AiAuditRunStatus;

  items: InternalAuditAiAuditItem[];

  constructor() {
    this.internalAuditId = 0;
    this.runStatus = AiAuditRunStatus.NotStarted;
    this.items = [];
  }

  isRunning(): boolean {
    return (
      this.runStatus === AiAuditRunStatus.InProgress ||
      this.items.some(
        (item) => item.status === AiAuditItemStatus.Waiting || item.status === AiAuditItemStatus.Running,
      )
    );
  }

  getItem(entityType: number, entityId: number): InternalAuditAiAuditItem | undefined {
    return this.items.find((item) => item.entityType === entityType && item.entityId === entityId);
  }
}

//
// The per-task AI results (the auditee's accordion).
//

export class AiControlAuditFinding {
  severity: number;

  title: string;

  observation: string;

  risk: string;

  recommendation: string;

  reference: string;

  constructor() {
    this.severity = 1;
    this.title = '';
    this.observation = '';
    this.risk = '';
    this.recommendation = '';
    this.reference = '';
  }
}

export class AiControlAuditCheckRow {
  checkKey: string;

  requirement: string;

  successCriteria: string;

  mode: string;

  evidenceStatus: string;

  // The evidence task of a one-time check, and whether it has one at all: the evidence for such a
  // criterion lives on that task, so naming it is what tells the auditor where to look.
  taskId?: number;

  bound: boolean;

  constructor() {
    this.checkKey = '';
    this.requirement = '';
    this.successCriteria = '';
    this.mode = '';
    this.evidenceStatus = '';
    this.taskId = undefined;
    this.bound = false;
  }
}

// The model's judgement of one document read during the documentation audit: which documents counted
// and which did not, so the document owner can fix the documentation set.
export class AiControlDocumentAssessment {
  linkId: number;

  name?: string;

  // High / Medium / Low / None (raw wire value; rendered through a translation)
  relevance: string;

  rationale: string;

  constructor() {
    this.linkId = 0;
    this.name = undefined;
    this.relevance = '';
    this.rationale = '';
  }
}

export class AiControlAuditReport {
  status: AiControlAuditReportStatus;

  // True when the report is a guard MESSAGE report: the status carries a reason, not a verdict.
  // The per-item equivalent of InternalAuditAiAuditItem's documentationAssessed / evidenceAssessed,
  // which the API derives from exactly this flag.
  messageOnly: boolean;

  controlCode: string;

  controlName: string;

  managementSummary: string;

  overallAssessment: string;

  // why the status is what it is, in two to four sentences; undefined for reports written before it existed
  statusRationale?: string;

  // documentation audit only; empty for the evidence audit and for older reports
  documentAssessments: AiControlDocumentAssessment[];

  markdownReport: string;

  findings: AiControlAuditFinding[];

  evidenceCheckRows: AiControlAuditCheckRow[];

  limitations: string[];

  constructor() {
    this.status = AiControlAuditReportStatus.NotReady;
    this.messageOnly = false;
    this.controlCode = '';
    this.controlName = '';
    this.managementSummary = '';
    this.overallAssessment = '';
    this.statusRationale = undefined;
    this.documentAssessments = [];
    this.markdownReport = '';
    this.findings = [];
    this.evidenceCheckRows = [];
    this.limitations = [];
  }
}

export class InternalAuditControlAuditResult {
  controlId: number;

  generatedOn: Date | undefined;

  documentation: AiControlAuditReport | undefined;

  evidence: AiControlAuditReport | undefined;

  constructor() {
    this.controlId = 0;
    this.generatedOn = undefined;
    this.documentation = undefined;
    this.evidence = undefined;
  }

  getWorstStatus(): AiControlAuditReportStatus | undefined {
    const item = new InternalAuditAiAuditItem();
    item.documentationStatus = this.documentation?.status;
    item.evidenceStatus = this.evidence?.status;

    return item.getWorstStatus();
  }

  // Whether the AI actually evaluated anything. The same rule the execute grid applies through
  // InternalAuditAiAuditItem, so the auditee's pane and the lead auditor's list cannot disagree
  // about the same result: a run whose modes all came back as guard message reports assessed
  // nothing and must not read as a verdict.
  hasAssessment(): boolean {
    return (
      (this.documentation !== undefined && !this.documentation.messageOnly) ||
      (this.evidence !== undefined && !this.evidence.messageOnly)
    );
  }

  // The worst verdict over the ASSESSED modes only; undefined when nothing was assessed.
  getWorstAssessedStatus(): AiControlAuditReportStatus | undefined {
    const item = new InternalAuditAiAuditItem();
    item.documentationStatus = this.documentation?.messageOnly === false ? this.documentation.status : undefined;
    item.evidenceStatus = this.evidence?.messageOnly === false ? this.evidence.status : undefined;

    return item.getWorstStatus();
  }

  // Merged view for "Take over result": the check verdicts of both modes, keyed by checkKey.
  getCheckRows(): AiControlAuditCheckRow[] {
    return [...(this.documentation?.evidenceCheckRows ?? []), ...(this.evidence?.evidenceCheckRows ?? [])];
  }

  // The verdict for one checkKey. A check can appear in several rows (one per monitoring series,
  // and once per mode): the worst inspected verdict is the honest one for the auditor.
  getCheckRowForKey(checkKey: string): AiControlAuditCheckRow | undefined {
    const rank = (status: string): number => {
      switch (status) {
        case 'Contradicted':
          return 6;
        case 'NotSatisfied':
          return 5;
        case 'PartiallySatisfied':
          return 4;
        case 'NotInspectable':
          return 3;
        case 'Satisfied':
          return 2;
        case 'Unknown':
          return 1;
        default:
          return 0;
      }
    };

    const matches = this.getCheckRows().filter(
      (row) => row.checkKey.toLowerCase() === checkKey.toLowerCase() && row.evidenceStatus,
    );
    if (matches.length === 0) return undefined;

    return matches.reduce((worst, row) => (rank(row.evidenceStatus) > rank(worst.evidenceStatus) ? row : worst));
  }

  getConclusionText(): string {
    const parts: string[] = [];
    if (this.documentation?.managementSummary) parts.push(this.documentation.managementSummary);
    if (this.documentation?.overallAssessment) parts.push(this.documentation.overallAssessment);
    if (this.evidence?.managementSummary) parts.push(this.evidence.managementSummary);
    if (this.evidence?.overallAssessment) parts.push(this.evidence.overallAssessment);

    return parts.join('\n\n');
  }
}

export class InternalAuditAiAuditReportLink {
  linkId: number;

  linkName: string;

  linkURL: string;

  constructor() {
    this.linkId = 0;
    this.linkName = '';
    this.linkURL = '';
  }
}

// Why no conclusion came back. Only NoInput and Failed carry a message of their own: the first is
// the auditor's own next step, the second is worth retrying. Everything else stays None.
export enum InternalAuditConclusionReason {
  None = 0,
  NoInput = 1,
  Failed = 2,
}

// The AI's proposed result for one audit execution task. The outcome is already validated and
// corrected server-side, so it can go straight into the editor; the conclusion is plain text.
// An empty conclusion means there is no suggestion, and reason says why.
export class InternalAuditConclusionSuggestion {
  outcome: AuditOutcome;

  conclusion: string;

  reason: InternalAuditConclusionReason;

  constructor() {
    this.outcome = AuditOutcome.NotAssessed;
    this.conclusion = '';
    this.reason = InternalAuditConclusionReason.None;
  }
}
